import { Dayjs } from 'dayjs';
export interface Carrier {
  id: string;
  name: string;
  image?: string;
  code: string;
  status: 'ready' | 'pending' | 'partial';
  totalShipments: number;
  totalInvoiceAmount: number;
  currency: string;
}

export interface AWB {
  id: string;
  number: string;
  awbNumber: string;
  carrierWeight: number;
  systemWeight: number;
  carrierCharges: number;
  systemCharges: number;
  date: string;
  origin: string;
  destination: string;
  currency: string;
  selected: boolean;
  status?: 'pending' | 'completed' | 'error';
}

export interface InvoiceData {
  invoiceDate: string;
  dueDate: string;
  selectedAWBs: AWB[];
  totalAmount: number;
}

export interface Shipment {
  shipmentNumber: string;
  awbNumber: string;
  createdAt: string;
  weight: number;
  currency: string;
  systemId: string;
  origin: string;
  destination: string;
  carrierId: string;
  serviceProviderId: string;
  code: string;
  amount: number;
  shipmentRouteType: string;
  purchaseInvoiceNumber?: string;
}

export interface ServiceProviderStatsMap {
  [serviceProviderId: string]: Shipment[];
}

export interface CarrierStats {
  _id: string;
  currency: string;
  code: string;
  amount: number;
  shipmentNumbers: string[];
  shipments: number;
}

export interface CarrierStatsMap {
  [carrierId: string]: CarrierStats;
}

export interface UnbilledShipmentsResponse {
  msg: string;
  data: Record<string, CarrierData>;
}

// Filters
export interface UnbilledShipmentsFilters {
  fromDate?: string | Date;
  toDate?: string | Date;
  systems?: string[];
  carriers?: string[];
}

export interface Shipment {
  shipmentNumber: string;
  createdAt: string;
  weight: number;
  currency: string;
  systemId: string;
  origin: string;
  destination: string;
  carrierId: string;
  serviceProviderId: string;
  code: string;
  amount: number;
  status: string;
  shipmentRouteType: string;
}

export interface CarrierData {
  carrierId: string;
  currency: string;
  code: string;
  name: string;
  amount: number;
  shipments: Shipment[];
  shipmentCount: number;
}

export interface FinalSubmissionPayload {
  carrier: Carrier;
  invoiceDate: Dayjs | string;
  dueDate: Dayjs | string;
  carrierReferenceNumber: string;
  invoiceLocation?: string;
  vendorLocation?: string;
  d365AccountDisplayValue?: string;
  awbs: AWB[];
}

export const formatShipmentNumber = (value: any): string => {
  if (value === null || value === undefined) return '';

  // If number (scientific case)
  if (typeof value === 'number') {
    return value.toLocaleString('fullwide', { useGrouping: false });
  }

  return value.toString().trim();
};

type InvoiceCharge = {
  name: string;
  amount: number;
};

type ShipmentInvoice = {
  shipmentNumber: string;
  awbNumber: string;
  unit: string;
  isTaxed: boolean;
  actualWeight: number;
  chargeableWeight: number;
  length: number;
  breadth: number;
  height: number;
  weight: number;
  numberOfBoxes: number;
  carrierReferenceNumber: string;
  charges: InvoiceCharge[];
};

type CarrierInvoicePayload = {
  invoices: ShipmentInvoice[];
  carrierId: string;
};

type GroupedShipmentInvoices = {
  carrierReferenceNumber: string;
  shipments: ShipmentInvoice[];
};

type TransformedCarrierInvoicePayload = {
  invoices: GroupedShipmentInvoices[];
  carrierId: string;
};

export const transformCarrierInvoicesPayload = (payload: CarrierInvoicePayload): TransformedCarrierInvoicePayload => {
  const groupedInvoices = payload.invoices.reduce<Record<string, GroupedShipmentInvoices>>((acc, invoice) => {
    const referenceNumber = invoice.carrierReferenceNumber;

    // Create group if not exists
    if (!acc[referenceNumber]) {
      acc[referenceNumber] = {
        carrierReferenceNumber: referenceNumber,
        shipments: [],
      };
    }

    // Add shipment into group
    acc[referenceNumber].shipments.push(invoice);

    return acc;
  }, {});

  return {
    carrierId: payload.carrierId,
    invoices: Object.values(groupedInvoices),
  };
};

export const getErrorDetails = (shipmentList: any, errorDetails?: any) => {
  try {
    if (Array.isArray(shipmentList)) {
      if (!shipmentList.length) return errorDetails || undefined;

      // New format → [{ msg, carrierReferenceNumber }]
      if (typeof shipmentList[0] === 'object' && shipmentList[0] !== null) {
        return shipmentList
          .map(item => {
            const carrierRef = item?.carrierReferenceNumber || 'Carrier Reference N/A';

            const message = typeof item?.msg === 'string' ? item.msg : JSON.stringify(item?.msg || 'Unknown error');

            return `Carrier Ref: ${carrierRef}\n${message}`;
          })
          .join('\n\n');
      }

      // Old format → ["123", "456"]
      return `Shipment${shipmentList.length > 1 ? 's' : ''} - ${shipmentList.join(', ')}`;
    }

    // String
    if (typeof shipmentList === 'string') {
      return shipmentList;
    }

    // Object
    if (shipmentList && typeof shipmentList === 'object') {
      return JSON.stringify(shipmentList, null, 2);
    }

    return errorDetails ? (typeof errorDetails === 'string' ? errorDetails : JSON.stringify(errorDetails)) : undefined;
  } catch (e) {
    console.error('Error formatting details:', e);

    return typeof errorDetails === 'string' ? errorDetails : 'Something went wrong while parsing error details.';
  }
};
